import { Fragment, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { fmtFechaHora } from "@/lib/datetime";
import PageHeader from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ListChecks, MagnifyingGlass, ShieldCheck, ShieldWarning, CaretDown, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import GuidedTour from "@/components/GuidedTour";
import TourHelpButton from "@/components/TourHelpButton";
import AuditoriaEjemplo from "@/components/AuditoriaEjemplo";
import { useGuidedTour } from "@/hooks/useGuidedTour";

const TOUR_STORAGE_KEY = "chkr_tour_auditoria_seen";

const TOUR_STEPS = [
  { target: "[data-tour='auditoria-verificar']", title: "Verifica la integridad", content: "Cada registro está encadenado con un hash del anterior. Este botón recorre la cadena y te dice si algún registro fue alterado." },
  { target: "[data-tour='auditoria-ejemplo']", title: "¿Cómo funciona la cadena?", content: "Aquí tienes un ejemplo con datos reales de cómo se enlazan los hashes y qué pasa exactamente cuando alguien altera un registro." },
  { target: "[data-tour='auditoria-filtros']", title: "Filtra por entidad", content: "Acota la bitácora por tipo de entidad (empleados, jornadas, usuarios…) o por tipo de acción (crear, actualizar…)." },
  { target: "[data-tour='auditoria-tabla']", title: "Bitácora de cambios", content: "Da clic en cualquier renglón para expandirlo y ver el valor anterior y nuevo del cambio. Ningún registro original se elimina físicamente." },
];

const ACTION_COLOR = {
  crear: "bg-emerald-100 text-emerald-800",
  actualizar: "bg-blue-100 text-blue-800",
  eliminar_logico: "bg-amber-100 text-amber-800",
  eliminar: "bg-red-100 text-red-800",
  baja: "bg-red-100 text-red-800",
  desactivar: "bg-red-100 text-red-800",
  autorizada: "bg-emerald-100 text-emerald-800",
  rechazada: "bg-red-100 text-red-800",
  importar: "bg-violet-100 text-violet-800",
  importar_lote: "bg-violet-100 text-violet-800",
  importar_registro: "bg-violet-100 text-violet-800",
  corregir: "bg-amber-100 text-amber-800",
};

export default function Auditoria() {
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({ entidad: "todas", accion: "" });
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const { active: tourActive, start: startTour, finish: finishTour } = useGuidedTour(TOUR_STORAGE_KEY);

  const load = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filters.entidad !== "todas") p.set("entidad", filters.entidad);
    if (filters.accion) p.set("accion", filters.accion);
    const { data } = await api.get("/auditoria?" + p.toString());
    setItems(data); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const verificar = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const { data } = await api.get("/auditoria/verificar");
      setVerifyResult(data);
      if (data.integro) toast.success("Bitácora íntegra: no se detectaron alteraciones");
      else toast.error("Se detectó una posible alteración en la bitácora");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setVerifying(false); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="auditoria-page">
      <PageHeader
        kicker="Trazabilidad"
        title="Auditoría"
        subtitle="Bitácora completa de cambios, encadenada con hash. Ningún registro original se elimina físicamente."
        actions={
          <div className="flex gap-2 items-center">
            <Button onClick={verificar} disabled={verifying} className="bg-brand hover:bg-brand-hover" data-testid="auditoria-verificar-btn" data-tour="auditoria-verificar">
              <ListChecks size={16} weight="bold" className="mr-2" />
              {verifying ? "Verificando…" : "Verificar integridad"}
            </Button>
            <TourHelpButton onClick={startTour} />
          </div>
        }
      />

      <GuidedTour steps={TOUR_STEPS} active={tourActive} onFinish={finishTour} />

      <AuditoriaEjemplo />

      {verifyResult && (
        <div
          className={`mb-4 rounded-md p-4 text-sm flex items-start gap-3 border ${
            verifyResult.integro ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"
          }`}
          data-testid="auditoria-verify-result"
        >
          {verifyResult.integro ? <ShieldCheck size={20} weight="bold" className="mt-0.5 shrink-0" /> : <ShieldWarning size={20} weight="bold" className="mt-0.5 shrink-0" />}
          <div>
            <div className="font-bold">
              {verifyResult.integro ? "Cadena de auditoría íntegra" : "Alteración detectada en la cadena de auditoría"}
            </div>
            <div className="text-xs mt-1">
              {verifyResult.entradas_verificadas} entradas encadenadas verificadas
              {verifyResult.entradas_heredadas_sin_cadena > 0 && ` · ${verifyResult.entradas_heredadas_sin_cadena} entradas anteriores a la activación del encadenamiento (sin esta protección)`}
            </div>
            {!verifyResult.integro && verifyResult.primer_registro_alterado && (
              <pre className="text-xs mt-2 bg-white/60 border border-red-200 rounded p-2 overflow-x-auto">
                {JSON.stringify(verifyResult.primer_registro_alterado, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}

      <div className="bg-slate-50 border border-slate-200 rounded-md p-4 mb-4 flex gap-3 flex-wrap items-end" data-tour="auditoria-filtros">
        <div>
          <Label className="label-uppercase text-[10px]">Entidad</Label>
          <Select value={filters.entidad} onValueChange={(v) => setFilters({ ...filters, entidad: v })}>
            <SelectTrigger className="h-9 w-48"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas</SelectItem>
              <SelectItem value="empresas">Empresas</SelectItem>
              <SelectItem value="centros_trabajo">Centros</SelectItem>
              <SelectItem value="empleados">Empleados</SelectItem>
              <SelectItem value="horarios">Horarios</SelectItem>
              <SelectItem value="registros_jornada">Jornadas</SelectItem>
              <SelectItem value="incidencias">Incidencias</SelectItem>
              <SelectItem value="usuarios">Usuarios</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-uppercase text-[10px]">Acción</Label>
          <Input className="h-9 w-48" placeholder="crear, actualizar…" value={filters.accion} onChange={(e) => setFilters({ ...filters, accion: e.target.value })} />
        </div>
        <Button onClick={load} className="bg-brand hover:bg-brand-hover h-9">
          <MagnifyingGlass size={14} className="mr-2" /> Filtrar
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-tour="auditoria-tabla">
        <div className="overflow-x-auto">
        <table className="data-table" data-testid="auditoria-table">
          <thead>
            <tr>
              <th></th>
              <th>Fecha</th>
              <th>Usuario</th>
              <th>Acción</th>
              <th>Entidad</th>
              <th>ID</th>
              <th>IP</th>
              <th>Motivo</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="text-center text-slate-500 py-8">Cargando…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={8} className="text-center text-slate-500 py-8">Sin eventos</td></tr>}
            {items.map((i) => (
              <Fragment key={i.id}>
                <tr className="cursor-pointer hover:bg-slate-50" onClick={() => setExpandedId(expandedId === i.id ? null : i.id)} data-testid={`auditoria-row-${i.id}`}>
                  <td className="w-6 text-slate-400">{expandedId === i.id ? <CaretDown size={14} /> : <CaretRight size={14} />}</td>
                  <td className="font-mono text-xs whitespace-nowrap">{fmtFechaHora(i.fecha)}</td>
                  <td>
                    <div className="text-sm font-semibold">{i.usuario_email}</div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{i.usuario_rol}</div>
                  </td>
                  <td><span className={`text-xs px-2 py-0.5 rounded font-bold uppercase tracking-wider ${ACTION_COLOR[i.accion] || "bg-slate-100 text-slate-700"}`}>{i.accion}</span></td>
                  <td className="text-xs font-mono">{i.entidad}</td>
                  <td className="text-xs font-mono text-slate-500">{(i.entidad_id || "").slice(0, 8)}</td>
                  <td className="text-xs font-mono text-slate-500">{i.ip || "—"}</td>
                  <td className="text-xs text-slate-600 max-w-xs truncate">{i.motivo || ""}</td>
                </tr>
                {expandedId === i.id && (
                  <tr>
                    <td colSpan={8} className="bg-slate-50 p-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <div className="label-uppercase text-[10px] mb-1 text-slate-500">Valor anterior</div>
                          <pre className="text-xs bg-white border border-slate-200 rounded p-2 overflow-x-auto max-h-64">{i.valor_anterior != null ? JSON.stringify(i.valor_anterior, null, 2) : "—"}</pre>
                        </div>
                        <div>
                          <div className="label-uppercase text-[10px] mb-1 text-slate-500">Valor nuevo</div>
                          <pre className="text-xs bg-white border border-slate-200 rounded p-2 overflow-x-auto max-h-64">{i.valor_nuevo != null ? JSON.stringify(i.valor_nuevo, null, 2) : "—"}</pre>
                        </div>
                      </div>
                      {i.hash && (
                        <div className="text-[10px] font-mono text-slate-400 mt-3">seq {i.seq} · hash {i.hash.slice(0, 16)}… · anterior {i.hash_anterior?.slice(0, 16)}…</div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}
